// src/utils/labOrderIdentity.js
//
// Pure helpers that keep the patient's identity and contact details OUT of what the chat assistant (the
// LLM provider) has to see or write when a lab test is ordered in chat.
//
// Before: the model read the patient's name, phone, email and address from the profile tools and typed
// them back into the create_order payload. Now the model only chooses WHAT is ordered (test items, slot,
// which saved address by id). The app overlays the real name / phone / email / age / gender and the chosen
// saved address from the patient's own profile, fetched with their login, and draws the review card from
// the same data. Anything the model puts in those identity fields is overwritten, so it cannot mistype a
// phone number or redirect an order.
//
// An address the patient typed into the chat for a pincode they have not used before is still passed
// through as given — the patient wrote it in the conversation, there is nothing to overlay.

const GENDERS = new Set(['MALE', 'FEMALE', 'OTHER']);

const upper = (v) => String(v ?? '').trim().toUpperCase();

export const fullName = (profile) =>
  [profile?.firstName, profile?.lastName].map((s) => String(s ?? '').trim()).filter(Boolean).join(' ');

const profileGender = (profile, fallback) => {
  const g = upper(profile?.gender);
  return GENDERS.has(g) ? g : fallback;
};

/**
 * The profile fields an order cannot go out without. The app fills these in from the profile, so if one is
 * missing the order must stop and the patient be asked — never fall back to a default (the order builder
 * would otherwise send age 30 / male to the lab, which changes the reference ranges used on the report).
 */
export function missingOrderFields(profile) {
  const missing = [];
  if (!String(profile?.email ?? '').includes('@')) missing.push('email');
  if (String(profile?.phone ?? '').replace(/\D/g, '').length < 10) missing.push('phone number');
  if (profile?.age == null || Number.isNaN(Number(profile.age))) missing.push('age');
  if (!GENDERS.has(upper(profile?.gender))) missing.push('gender');
  return missing;
}

/**
 * Overlays the patient's real identity onto a slot-search request. Test items and the pincode stay as the
 * model chose them.
 */
export function applyPatientToSlotSearch(slotSearchRequest, profile) {
  if (!slotSearchRequest || !profile) return slotSearchRequest;
  const req = { ...slotSearchRequest };
  if (Array.isArray(req.patients) && req.patients.length > 0) {
    const [first, ...rest] = req.patients;
    req.patients = [
      {
        ...first,
        name: fullName(profile) || first.name,
        gender: profileGender(profile, first.gender),
        age: profile.age ?? first.age,
      },
      ...rest,
    ];
  }
  return req;
}

/**
 * Picks the saved address for an order:
 *   1. the one the model named by id,
 *   2. otherwise (when the model did not supply an address of its own) the default saved address at the
 *      pincode the slots were searched at, then any saved address at that pincode.
 * Returns null when nothing applies, leaving whatever the order already carries.
 */
export function pickAddress(addresses, { addressId, searchedPincode, hasOwnAddress } = {}) {
  const list = Array.isArray(addresses) ? addresses : [];
  if (addressId) return list.find((a) => String(a.id) === String(addressId)) || null;
  if (hasOwnAddress) return null;
  const pin = String(searchedPincode ?? '').replace(/\D/g, '');
  if (!pin) return null;
  const atPin = list.filter((a) => String(a.zipCode ?? '').replace(/\D/g, '') === pin);
  return atPin.find((a) => a.isDefault) || atPin[0] || null;
}

/** Saved address in the shape the existing order builder maps (street/zipCode -> addressLine1/pincode). */
export const savedAddressToOrderAddress = (a) => ({
  street: a.street,
  city: a.city,
  state: a.state,
  zipCode: a.zipCode,
  country: a.country || 'India',
  landmark: a.landmark,
});

/**
 * Overlays the patient's real identity (and the chosen saved address, if any) onto a create_order request.
 * Only the FIRST patient entry is overwritten, and only its identity fields; its items are the model's
 * test choice and are kept. Top-level email and contactNumber always come from the profile.
 */
export function applyPatientToOrder(orderRequest, profile, savedAddress) {
  const o = { ...(orderRequest || {}) };
  if (profile) {
    if (profile.email) o.email = profile.email;
    if (profile.phone) o.contactNumber = profile.phone;
    const patients = Array.isArray(o.patients) && o.patients.length > 0 ? o.patients : [{}];
    const [first, ...rest] = patients;
    o.patients = [
      {
        ...first,
        name: fullName(profile) || first.name,
        age: profile.age ?? first.age,
        gender: profileGender(profile, first.gender),
        email: profile.email || first.email,
        contactNumber: profile.phone || first.contactNumber,
      },
      ...rest,
    ];
  }
  if (savedAddress) o.address = savedAddressToOrderAddress(savedAddress);
  return o;
}

/** What the model is shown for address choices: enough to pick one, never the street text. */
export const compactAddressOptions = (addresses) =>
  (Array.isArray(addresses) ? addresses : []).map((a) => ({
    id: a.id,
    addressType: a.addressType,
    city: a.city,
    zipCode: a.zipCode,
    isDefault: !!a.isDefault,
  }));

/** Saved addresses that share a pincode — the only ones a booked slot can move between. */
export const sameResolvedPincode = (addresses, pincode) => {
  const pin = String(pincode ?? '').replace(/\D/g, '');
  return (Array.isArray(addresses) ? addresses : []).filter(
    (a) => String(a.zipCode ?? '').replace(/\D/g, '') === pin,
  );
};

const orderAddressLine = (a) =>
  [a?.addressLine1, a?.addressLine2 && a.addressLine2 !== a.addressLine1 ? a.addressLine2 : null, a?.landmark, a?.city, a?.state, a?.pincode]
    .map((s) => (s == null ? '' : String(s).trim()))
    .filter(Boolean)
    .join(', ');

const savedAddressLine = (a) =>
  [a?.street, a?.landmark, a?.city, a?.state, a?.zipCode]
    .map((s) => (s == null ? '' : String(s).trim()))
    .filter(Boolean)
    .join(', ');

/** One printable line for an address, from either the saved shape or the order (Thyrocare) shape. */
export const formatAddress = (a) => (a && a.addressLine1 !== undefined ? orderAddressLine(a) : savedAddressLine(a));

/**
 * Re-points a finished order payload at another saved address. Mirrors what the order builder does to a
 * saved address (street -> addressLine1, zipCode -> pincode, city/state upper-case). Only used for saved
 * addresses at the SAME pincode as the booked slot, so slot availability is unaffected.
 */
export function applySavedAddressToOrderPayload(orderPayload, saved) {
  const addr = {
    addressLine1: String(saved.street || 'Home Collection').trim(),
    city: upper(saved.city) || 'BANGALORE',
    state: upper(saved.state) || 'KARNATAKA',
    country: saved.country || 'India',
    pincode: String(saved.zipCode ?? orderPayload?.address?.pincode ?? ''),
  };
  if (saved.landmark) addr.landmark = saved.landmark;
  return { ...orderPayload, address: addr };
}

/** The review the patient sees on the card, built from the finished order and the profile. */
export function buildOrderSummary(orderPayload, profile) {
  const p = orderPayload?.patients?.[0];
  return {
    patientName: fullName(profile) || p?.name || '',
    phone: profile?.phone || '',
    email: profile?.email || orderPayload?.email || '',
    addressLine: formatAddress(orderPayload?.address),
    pincode: orderPayload?.address?.pincode || '',
    date: orderPayload?.appointment?.date || '',
    startTime: orderPayload?.appointment?.startTime || '',
    items: (p?.items || []).map((i) => i.name).filter(Boolean),
  };
}
